import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './data/http';
import { callbackFromLocation, finishLogin, logout, startLogin } from './lib/auth';
import { deletionFromLocation, finishDeletion } from './lib/account';
import { readPlans, type PlansResult } from './data/plans';
import { readProfile, readSession } from './data/profile';
import type { Profile as ProfileData } from './types';
import { tabPath, useRoute, type Tab } from './lib/route';
import { ToastHost } from './components/Toast';
import { Sidebar } from './components/Sidebar';
import { Visits } from './components/Visits';
import { Plans } from './components/Plans';
import { Plan } from './components/Plan';
import { Claim } from './components/Claim';
import { stashPending, takePending } from './data/claim';
import { Doctors } from './components/Doctors';
import { Profile } from './components/Profile';
import { useToast } from './lib/toast';

/* ОБОЛОЧКА КАБИНЕТА.

   ЧЕТЫРЕ СОСТОЯНИЯ, и каждое видно пациенту:
     `boot`  — спрашиваем `read/me`: вошёл или нет. До ответа не показываем ни
               кабинет, ни экран входа — мелькание «войдите» у вошедшего хуже
               задержки в четверть секунды;
     `login` — не вошёл: экран входа;
     `code`  — вернулись от Keycloak с кодом, меняем его на куки;
     `in`    — вошёл: оболочка с разделами;
     `gone`  — аккаунт удалён (Keycloak подтвердил): прощание, кабинета нет;
     `unsure`— вернулись после удаления, но подтвердить его не вышло: повтор.
   (`deleting` — пересылаем гейтвею итог удаления, как `code` — код входа.)

   БЕЗ ГЕЙТВЕЯ (`API_BASE` пуст) КАБИНЕТ СЧИТАЕТСЯ ОТКРЫТЫМ, НО ПУСТЫМ. Вход
   там недоступен по определению — спрашивать некого, — а разделы показывают
   «нет данных». Выдуманных данных в кабинете нет НИ В ОДНОМ режиме (решение
   человека 12.09): подписанные «макетом» строки всё равно читались бы пациентом
   как его записи, а цифра в меню — как факт.

   ДАННЫЕ ГРУЗЯТСЯ ОДИН РАЗ НА ВХОД, а не на каждый раздел: профиль нужен и
   боковому меню, и экрану профиля, а планы — и разделу планов, и точке в меню.
   Перезапрашивать их при переключении разделов значило бы дёргать сеть на каждый
   щелчок по меню. */

type Phase = 'boot' | 'login' | 'code' | 'in' | 'deleting' | 'gone' | 'unsure';

type Deletion = { ticket: string; status: string | null };

export function App() {
    return (
        <ToastHost>
            <Shell />
        </ToastHost>
    );
}

function Shell() {
    const toast = useToast();
    const { route, go, openPlan } = useRoute();
    const [phase, setPhase] = useState<Phase>('boot');
    const [problem, setProblem] = useState('');
    const [profile, setProfile] = useState<ProfileData>({
        surname: '',
        name: '',
        fathername: '',
        phone: '',
        email: '',
    });
    const [plans, setPlans] = useState<PlansResult>({ state: 'ok', plans: [] });
    // Итог удаления, который не удалось подтвердить: держим его для повтора.
    const [unconfirmed, setUnconfirmed] = useState<Deletion | null>(null);

    const load = useCallback(async () => {
        const [p, pl] = await Promise.all([readProfile(), readPlans()]);
        setProfile(p);
        setPlans(pl);
    }, []);

    /* ЗАГРУЗКА ПРИ СТАРТЕ. Возврат от Keycloak разбирается ЗДЕСЬ ЖЕ, а не
       отдельной страницей: код одноразовый, и обменять его надо до первого
       рендера кабинета — иначе пациент увидит экран входа, будучи вошедшим. */
    useEffect(() => {
        let alive = true;

        async function boot() {
            // Витрина: сети нет, вход недоступен, показываем кабинет с макетными
            // данными — иначе оболочка недосягаема вовсе.
            if (!API_BASE) {
                await load();
                if (alive) setPhase('in');
                return;
            }

            /* ВОЗВРАТ ПОСЛЕ «УДАЛИТЬ АККАУНТ» (lib/account.ts). Разбирается до
               проверки сессии: при удалённом аккаунте её уже нет, и экран входа
               вместо итога был бы враньём. Отмена и отказ — не удаление: пациент
               остаётся в кабинете, на профиле, с объяснением. */
            let notice = '';
            if (route.kind === 'deleted') {
                const { ticket, status } = deletionFromLocation();
                window.history.replaceState(null, '', tabPath('visits'));
                if (ticket !== null) {
                    if (alive) setPhase('deleting');
                    const res = await finishDeletion(ticket, status);
                    if (!alive) return;
                    if (res.kind === 'deleted') {
                        // Куки стирает только ответ гейтвея; его отказ здесь не важен —
                        // пользователя, которому они принадлежали, больше нет.
                        void logout();
                        setPhase('gone');
                        return;
                    }
                    if (res.kind === 'retry') {
                        setUnconfirmed({ ticket, status });
                        setProblem(res.message);
                        setPhase('unsure');
                        return;
                    }
                    notice = res.kind === 'cancelled' ? 'удаление отменено' : res.message;
                }
            }

            if (route.kind === 'login') {
                const { code, state, error } = callbackFromLocation();
                // Адрес чистим СРАЗУ: код одноразовый, и перезагрузка страницы с
                // ним в строке дала бы «код не подошёл» на пустом месте.
                window.history.replaceState(null, '', tabPath('visits'));
                if (error !== null) {
                    if (alive) {
                        setPhase('login');
                        setProblem(error === 'access_denied' ? 'вход отменён' : 'страница входа отказала');
                    }
                    return;
                }
                if (code !== null) {
                    if (alive) setPhase('code');
                    const res = await finishLogin(code, state);
                    if (!alive) return;
                    if (!res.ok) {
                        setPhase('login');
                        setProblem(res.message);
                        return;
                    }
                }
            }

            const s = await readSession();
            if (!alive) return;
            if (s === null) {
                /* ПРИШЁЛ ПО ССЫЛКЕ НА ПЛАН И НЕ ВОШЁЛ — секрет откладывается
                   ЗДЕСЬ (правка 16.09 по найденному дефекту). Экран приёма
                   ссылки живёт в фазе `in`, то есть у анонимного не выполняется
                   вовсе, и откладывать секрет там было некому: человек уходил на
                   вход, возвращался в пустой кабинет, а ссылка умирала вместе с
                   адресом. План открывался только со второго клика по ней. */
                if (route.kind === 'claim') stashPending(route.token);
                setPhase('login');
                return;
            }
            /* Пациент пришёл по ссылке на план, был анонимен и ушёл на вход.
               Keycloak возвращает ВСЕГДА на `/login` — адрес возврата у клиента
               один, — поэтому секрет мы проносим через вход сами и здесь же
               возвращаем человека туда, куда он шёл. Уход со страницы, а не
               setState: дальше всё делает экран приёма ссылки. */
            const pending = takePending();
            if (pending !== null) {
                window.location.replace(`/care/${encodeURIComponent(pending)}`);
                return;
            }
            await load();
            if (!alive) return;
            setPhase('in');
            if (notice !== '') {
                go('profile');
                toast(notice);
            }
        }

        void boot();
        return () => {
            alive = false;
        };
        // Один раз на монтирование: `route` читается внутри и меняться до входа
        // не может — Keycloak возвращает перезагрузкой страницы.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    if (phase === 'gone') {
        return (
            <div className="gate">
                <div className="gbox">
                    <div className="wm">aventrae</div>
                    <h1>Аккаунт удалён</h1>
                    <p>Войти в кабинет с этим номером больше нельзя. Спасибо, что были с нами.</p>
                </div>
            </div>
        );
    }

    if (phase === 'unsure' && unconfirmed !== null) {
        return <Unconfirmed deletion={unconfirmed} problem={problem} onDeleted={() => setPhase('gone')} />;
    }

    if (phase === 'boot' || phase === 'code' || phase === 'deleting') {
        return (
            <div className="gate">
                <div className="gbox">
                    <div className="wm">aventrae</div>
                    <h1>
                        {phase === 'code' ? 'Проверяем вход' : phase === 'deleting' ? 'Проверяем удаление' : 'Загружаем кабинет'}
                    </h1>
                    <p>Секунду.</p>
                </div>
            </div>
        );
    }

    /* Экран входа знает, зачем его открыли: пациент пришёл по ссылке врача и
       ждёт план, а не «кабинет». Текст про визиты и профиль в этот момент читается
       как «я не туда попал». */
    if (phase === 'login') return <Gate problem={problem} forPlan={route.kind === 'claim'} />;

    /* В ЧИПЕ — «Имя Фамилия», без отчества: так в макете, и полное ФИО там не
       помещается — обрезается посреди фамилии. Отчество врачу нужно в заявке, а
       не в шапке кабинета; правится оно на экране профиля, где стоит целиком. */
    const shortName = [profile.name, profile.surname].filter(Boolean).join(' ');
    /* Страница одного плана живёт ВНУТРИ раздела «планы ухода»: в меню
       подсвечивается он, а адрес ведёт на конкретный план. */
    const tab: Tab = route.kind === 'tab' ? route.tab : route.kind === 'plan' ? 'care' : 'visits';
    const careDot =
        plans.state === 'ok' && plans.plans.some((p) => !p.completed && p.nextDay !== null);

    return (
        <div className="shell">
            <Sidebar
                tab={tab}
                onGo={go}
                fullName={shortName}
                phone={profile.phone}
                /* СЧЁТЧИКИ ТОЛЬКО ИЗ НАСТОЯЩИХ ДАННЫХ. У визитов и врачей источника
                   нет — значит и числа взяться неоткуда; нуль меню не рисует
                   вовсе. Раньше тут стояли длины выдуманных массивов, и цифра в
                   меню выглядела фактом с любого экрана, даже если пациент в
                   раздел не заходил. */
                counts={{ visits: 0, doctors: 0, careDot }}
                onLogout={async () => {
                    const ok = await logout();
                    // Куки стирает ОТВЕТ гейтвея, поэтому состояние правится
                    // перезагрузкой: «оптимистично» показать экран входа значило бы
                    // соврать, если запрос не дошёл.
                    if (ok) window.location.assign(tabPath('visits'));
                    else toast('не удалось выйти, попробуйте ещё раз');
                }}
            />
            <main className="main">
                {route.kind === 'claim' ? (
                    <Claim token={route.token} />
                ) : route.kind === 'plan' ? (
                    <Plan id={route.id} onBack={() => go('care')} />
                ) : (
                    <>
                        {tab === 'visits' && <Visits plans={plans} />}
                        {tab === 'care' && <Plans result={plans} onOpen={openPlan} />}
                        {tab === 'doctors' && <Doctors />}
                        {tab === 'profile' && (
                            <Profile
                                initial={profile}
                                onSaved={(p) => setProfile((prev) => ({ ...prev, ...p }))}
                                onEmailChanged={(email) => setProfile((prev) => ({ ...prev, email }))}
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

/* ЭКРАН ВХОДА. Своей вёрстки в макете нет — макет начинается с уже вошедшего
   пациента, — поэтому взята оболочка карточки кабинета: тот же бренд, та же
   тень, та же кнопка. Номер и код вводятся НЕ ЗДЕСЬ: их спрашивает Keycloak в
   пациентской теме, и второе поле номера у нас было бы тем же дублем, который
   уже убирали на публичной странице. */
function Gate({ problem, forPlan }: { problem: string; forPlan: boolean }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(problem);

    return (
        <div className="gate">
            <div className="gbox">
                <div className="wm">aventrae</div>
                <h1>{forPlan ? 'План ухода' : 'Личный кабинет'}</h1>
                <p>
                    {forPlan
                        ? 'Чтобы открыть план, войдите по номеру телефона — тому, который вы давали в клинике. После входа план откроется сам.'
                        : 'Здесь ваши визиты, планы ухода после процедур и профиль. Вход по номеру телефона.'}
                </p>
                <button
                    type="button"
                    className="wide pri"
                    disabled={busy}
                    onClick={async () => {
                        setBusy(true);
                        setError('');
                        const res = await startLogin();
                        // Успех — это уход со страницы, сюда управление не вернётся.
                        setBusy(false);
                        setError(res.message);
                    }}
                >
                    {busy ? 'открываем вход…' : 'войти'}
                </button>
                {error !== '' && <p className="gerr">{error}</p>}
            </div>
        </div>
    );
}

/* УДАЛЕНИЕ НЕ ПОДТВЕРДИЛОСЬ: Keycloak вернул пациента, но гейтвей не дозвался
   его проверить. Удалён ли аккаунт — неизвестно, поэтому ни «удалён», ни кабинет:
   только повтор. Он безопасен — гейтвей отмечает лишь то, что подтвердил Keycloak. */
function Unconfirmed({
    deletion,
    problem,
    onDeleted,
}: {
    deletion: Deletion;
    problem: string;
    onDeleted: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(problem);

    return (
        <div className="gate">
            <div className="gbox">
                <div className="wm">aventrae</div>
                <h1>Проверяем удаление</h1>
                <p>Не получилось убедиться, что аккаунт удалён. Попробуйте проверить ещё раз.</p>
                <button
                    type="button"
                    className="wide pri"
                    disabled={busy}
                    onClick={async () => {
                        setBusy(true);
                        setError('');
                        const res = await finishDeletion(deletion.ticket, deletion.status);
                        setBusy(false);
                        if (res.kind === 'deleted') {
                            void logout();
                            onDeleted();
                        } else if (res.kind === 'cancelled') {
                            window.location.assign(tabPath('profile'));
                        } else {
                            setError(res.message);
                        }
                    }}
                >
                    {busy ? 'проверяем…' : 'проверить ещё раз'}
                </button>
                {error !== '' && <p className="gerr">{error}</p>}
            </div>
        </div>
    );
}
